/**
 * PremiumButton.jsx
 * Botão premium com variantes inspiradas no Linear App
 * 
 * Variantes:
 * - primary: Ação principal (teal)
 * - secondary: Ação secundária (cinza)
 * - danger: Ação destrutiva (vermelho)
 * - ghost: Ação terciária (transparente)
 * - outline: Borda accent
 * - link: Estilo de link
 * 
 * Tamanhos: xs, sm, md, lg, xl
 * 
 * Uso:
 *   <PremiumButton variant="primary" size="md" startIcon={<SaveIcon />}>
 *     Salvar
 *   </PremiumButton>
 */

import React from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { usePremiumTokens } from '../../theme/usePremiumTokens';

const PremiumButton = React.forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  startIcon,
  endIcon,
  loading = false,
  disabled = false,
  fullWidth = false,
  iconOnly = false,
  onClick,
  type = 'button',
  href,
  target,
  sx: customSx,
  ...props
}, ref) => {
  const tokens = usePremiumTokens();

  // Safe token access with fallbacks
  const buttonTokens = tokens?.button?.[variant] || tokens?.button?.primary || {
    bg: '#3b82f6',
    bgHover: '#2563eb',
    bgActive: '#1d4ed8',
    text: '#ffffff',
    textHover: '#ffffff',
    border: 'transparent',
    borderHover: 'transparent',
  };

  const defaultSizes = {
    xs: { height: 24, fontSize: '12px', padding: '4px 8px', iconSize: '14px' },
    sm: { height: 28, fontSize: '13px', padding: '4px 12px', iconSize: '14px' },
    md: { height: 36, fontSize: '14px', padding: '6px 16px', iconSize: '16px' },
    lg: { height: 44, fontSize: '16px', padding: '8px 24px', iconSize: '18px' },
    xl: { height: 52, fontSize: '18px', padding: '10px 32px', iconSize: '20px' },
  };
  const sizeConfig = tokens?.button?.sizes?.[size] || defaultSizes[size] || defaultSizes.md;

  // Safe typography/radius/transition access
  const fontWeightMedium = tokens?.typography?.fontWeight?.medium || 500;
  const fontFamily = tokens?.typography?.fontFamily?.body || 'Inter, system-ui, sans-serif';
  const radiusMd = tokens?.radius?.md || '8px';
  const transitionAll = tokens?.transitions?.all || 'all 0.2s ease';
  const letterSpacingNormal = tokens?.typography?.letterSpacing?.normal || '0';
  const focusRingShadow = tokens?.shadows?.focusRing || '0 0 0 2px rgba(59, 130, 246, 0.3)';

  // Calcula padding para iconOnly
  const iconOnlyPadding = {
    xs: '0 6px',
    sm: '0 7px',
    md: '0 10px',
    lg: '0 13px',
    xl: '0 16px',
  };

  const baseStyles = {
    height: sizeConfig.height,
    minWidth: iconOnly ? sizeConfig.height : 'auto',
    padding: iconOnly ? iconOnlyPadding[size] : sizeConfig.padding,
    fontSize: sizeConfig.fontSize,
    fontWeight: fontWeightMedium,
    fontFamily,
    textTransform: 'none',
    borderRadius: radiusMd,
    backgroundColor: buttonTokens.bg,
    color: buttonTokens.text,
    border: buttonTokens.border !== 'transparent'
      ? `1px solid ${buttonTokens.border}`
      : '1px solid transparent',
    boxShadow: 'none',
    transition: transitionAll,
    lineHeight: 1,
    letterSpacing: letterSpacingNormal,

    // Hover
    '&:hover': {
      backgroundColor: buttonTokens.bgHover,
      color: buttonTokens.textHover || buttonTokens.text,
      borderColor: buttonTokens.borderHover || buttonTokens.border,
      boxShadow: 'none',
    },

    // Active/Pressed
    '&:active': {
      backgroundColor: buttonTokens.bgActive,
      transform: 'scale(0.98)',
    },

    // Focus
    '&:focus-visible': {
      outline: 'none',
      boxShadow: focusRingShadow,
    },

    // Disabled
    '&:disabled, &.Mui-disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      backgroundColor: buttonTokens.bg,
      color: buttonTokens.text,
      borderColor: buttonTokens.border,
    },

    // Icon styling
    '& .MuiButton-startIcon': {
      marginRight: iconOnly ? 0 : '8px',
      marginLeft: 0,
      '& > *:nth-of-type(1)': {
        fontSize: sizeConfig.iconSize,
      },
    },

    '& .MuiButton-endIcon': {
      marginLeft: '8px',
      marginRight: 0,
      '& > *:nth-of-type(1)': {
        fontSize: sizeConfig.iconSize,
      },
    },

    // Loading spinner
    '& .premium-button-spinner': {
      width: sizeConfig.iconSize,
      height: sizeConfig.iconSize,
    },
  };

  // Estilos específicos para variante link
  const linkStyles = variant === 'link' ? {
    padding: 0,
    minWidth: 'auto',
    height: 'auto',
    textDecoration: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  } : {};

  // Merge dos estilos
  const mergedSx = {
    ...baseStyles,
    ...linkStyles,
    ...customSx,
  };

  // Renderiza loading spinner ou children
  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: iconOnly ? 0 : 1 }}>
          <CircularProgress 
            size={parseInt(sizeConfig.iconSize)} 
            color="inherit" 
            className="premium-button-spinner"
          />
          {!iconOnly && children && <span>{children}</span>}
        </Box>
      );
    }
    return children;
  };

  // Se tiver href, renderiza como link
  const Component = href ? 'a' : Button;
  const linkProps = href ? { href, target, rel: target === '_blank' ? 'noopener noreferrer' : undefined } : {};

  return (
    <Button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      startIcon={!loading && startIcon}
      endIcon={!loading && endIcon}
      sx={mergedSx}
      component={href ? 'a' : 'button'}
      {...linkProps}
      {...props}
    >
      {renderContent()}
    </Button>
  );
});

PremiumButton.displayName = 'PremiumButton';

export default PremiumButton;

// ============================================================================
// VARIANTES PRÉ-CONFIGURADAS
// ============================================================================

/**
 * Botão de ação primária
 */
export const PrimaryButton = React.forwardRef((props, ref) => (
  <PremiumButton ref={ref} variant="primary" {...props} />
));
PrimaryButton.displayName = 'PrimaryButton';

/**
 * Botão de ação secundária
 */
export const SecondaryButton = React.forwardRef((props, ref) => (
  <PremiumButton ref={ref} variant="secondary" {...props} />
));
SecondaryButton.displayName = 'SecondaryButton';

/**
 * Botão de ação destrutiva/perigosa
 */
export const DangerButton = React.forwardRef((props, ref) => (
  <PremiumButton ref={ref} variant="danger" {...props} />
));
DangerButton.displayName = 'DangerButton';

/**
 * Botão ghost (transparente)
 */
export const GhostButton = React.forwardRef((props, ref) => (
  <PremiumButton ref={ref} variant="ghost" {...props} />
));
GhostButton.displayName = 'GhostButton';

/**
 * Botão outline
 */
export const OutlineButton = React.forwardRef((props, ref) => (
  <PremiumButton ref={ref} variant="outline" {...props} />
));
OutlineButton.displayName = 'OutlineButton';

/**
 * Botão estilo link
 */
export const LinkButton = React.forwardRef((props, ref) => (
  <PremiumButton ref={ref} variant="link" {...props} />
));
LinkButton.displayName = 'LinkButton';

/**
 * Botão apenas com ícone
 */
export const IconButton = React.forwardRef(({ icon, ...props }, ref) => (
  <PremiumButton ref={ref} iconOnly startIcon={icon} {...props} />
));
IconButton.displayName = 'IconButton';
